/**
 * BenchmarkScorecard — the report's headline numbers for the target company:
 * AI visibility (share-of-voice), recommendation rate, and run-to-run
 * stability. Styled like ScoreCard so the report reads as one product.
 */
import type { BenchmarkAggregate, BenchmarkRun } from '@aurora/shared';

function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function band(value: number): string {
  if (value >= 0.75) return 'strong';
  if (value >= 0.5) return 'solid';
  if (value >= 0.25) return 'moderate';
  return 'low';
}

interface MetricProps {
  label: string;
  value: number;
  why: string;
}

function Metric({ label, value, why }: MetricProps): JSX.Element {
  return (
    <div className={`score-card band-${band(value)}`}>
      <div className="score-head">
        <span className="score-label">{label}</span>
        <span className="score-value">{pct(value)}</span>
      </div>
      <div className="score-bar" aria-hidden>
        <div className="score-bar-fill" style={{ width: pct(value) }} />
      </div>
      <p className="score-why">{why}</p>
    </div>
  );
}

export function BenchmarkScorecard({
  run,
  aggregate,
}: {
  run: BenchmarkRun;
  aggregate: BenchmarkAggregate;
}): JSX.Element {
  const target = aggregate.shareOfVoice.entries.find((e) => e.isTarget);
  const visibility = target?.visibilityRate ?? 0;
  const recommendation = target?.recommendationRate ?? 0;
  const stability = aggregate.stability.overallAgreement;
  const cells = target?.scoredCells ?? 0;

  return (
    <div className="score-grid">
      <Metric
        label="AI visibility"
        value={visibility}
        why={`${run.companyName} appeared (mention, citation, or recommendation) in ${pct(visibility)} of ${cells} scored answers across ${run.modelPanel.length} model(s).`}
      />
      <Metric
        label="Recommendation rate"
        value={recommendation}
        why={`AI actively recommended ${run.companyName} in ${pct(recommendation)} of scored answers.`}
      />
      <Metric
        label="Result stability"
        value={stability}
        why={`Across repeat runs and models, the target's classification agreed ${pct(stability)} of the time — higher means more reproducible results.`}
      />
    </div>
  );
}
